import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { claimReferralRewardApi } from '@api'
import { BackButton, Loader, MainButton, View } from '@ds'
import { Lines, MainLayout, useStarAnim } from '@shared'
import {
  useInventoryActions,
  useReferralsFriendsActions,
  useReferralsStore,
  UserReferralsRewardItem,
  useSkinsActions,
} from '@store'

import { InviteLink } from '../profile/components/InviteLink'
import { type ConditionStep } from './constants'
import styles from './ReferralsView.module.scss'
import { Face } from './components/Face'
import { MilestoneItem } from './components/MilestoneItem'
import { StarAnim } from './components/StarAnim'
import { StarAnimProvider } from './providers/StarAnimProvider'

export const ReferralsView = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { starAnim } = useStarAnim()
  const { fetchSkins } = useSkinsActions()
  const { fetchFriends } = useReferralsFriendsActions()
  const { friends, rewards, ratingForReferrals, isLoading } = useReferralsStore()
  const { getItems } = useInventoryActions()

  const hasReferrals = friends.length > 0

  const handleClaim = async (milestoneId: number) => {
    const { ok, data } = await claimReferralRewardApi(milestoneId)
    if (ok && data) {
      getItems()
      fetchFriends()
    }
  }

  useEffect(() => {
    fetchFriends()
  }, [fetchFriends])

  useEffect(() => {
    fetchSkins()
  }, [])

  if (isLoading) {
    return <Loader />
  }

  return (
    <StarAnimProvider starAnim={starAnim}>
      <MainLayout showInventory={false} isNavbarFadeble>
        {(scrollRef) => (
          <>
            <BackButton onClick={() => {
              navigate(-1)
              window.location.reload()
            }} />
            <MainButton hidden />
            <View fadeIn className={styles.view} ref={scrollRef}>

              <div className={styles.header}>
                <Lines className={styles.lines} />
                <StarAnim width={180} height={180} className={styles.starAnim} />
                <h1 className={styles.title}>Your referrals:</h1>
              </div>

              <div className={styles.stats}>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>
                    <span className={styles.statNumber}>{friends.length}</span>

                    <div className={styles.statFace}>
                      <Face faceType={hasReferrals ? 'smile' : 'sad'} />
                    </div>

                  </div>
                  <span className={styles.statLabel}>Friends</span>
                </div>
                <span className={styles.equals}>=</span>
                <div className={classNames(styles.statCard, styles.statCardTwoLineLabel)}>
                  <div className={styles.statValue}>
                    <span className={classNames(styles.statNumber, styles.statNumber__frtiendsRating)}>{ratingForReferrals}</span>
                  </div>
                  <span className={styles.statLabel}>Rating <br /> for friends</span>
                </div>
              </div>

              <InviteLink compact className={styles.inviteRow} />

              <p className={styles.description}>
                {t('referrals.description')}<br />
                <span className={styles.descriptionCondition}>{t('referrals.descriptionCondition')}</span>
              </p>

              <div className={styles.milestones}>
                {rewards.map((reward: UserReferralsRewardItem, i: number) => {
                  const next = rewards[i + 1]
                  const isNextActive = next ? (next.is_claimed || next.is_available) : false
                  return (
                    <MilestoneItem
                      key={reward.milestone_id}
                      reward={reward}
                      isFirst={i === 0}
                      isLast={i === rewards.length - 1}
                      isNextActive={isNextActive}
                      onClaim={handleClaim}
                      step={(Math.min(i + 1, 8) as ConditionStep)}
                    />
                  )
                })}
              </div>

            </View>
          </>
        )}
      </MainLayout>
    </StarAnimProvider>
  )
}
